import { useEffect, useState, type MouseEvent } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CircularProgress from "@/components/common/CircularProgress";
import { usePsychologistData } from "@/hooks/usePsychologistData";
import { useBlockHoliday } from "@/hooks/useBlockHoliday";
import { timezoneToCountryCode } from "@/utils/timezoneToCountry";
import { countryNameFromTimezone, fetchPublicHolidays, formatHolidayDay } from "@/utils/holidays";
import { FERIADOS_CHANGED_EVENT, loadFeriadosTrabaja, setFeriadoTrabaja } from "@/utils/holidayDecisions";
import { isHolidayFullyBlocked } from "@/pages/psychologist/tabs/comunidad/holidaySessions";
import { activityTracker } from "@/utils/activityTracker";

type Variant = "chip" | "dot" | "banner";

interface Props {
  /** YYYY-MM-DD del día (en la zona horaria de la psicóloga). */
  date: string;
  /** chip = cabecera de la semanal y celda de la mensual · dot = marca chiquita sin acción
   *  (celdas de celular, donde toda la celda ya es un botón) · banner = vista diaria. */
  variant?: Variant;
}

/** Nombre del feriado de ese día en el país de la psicóloga (próximos feriados), o null. */
function useHolidayName(date: string, timezone: string): string | null {
  const [name, setName] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const country = timezoneToCountryCode(timezone);
    if (!country) {
      setName(null);
      return;
    }
    fetchPublicHolidays(country)
      .then((list) => { if (!cancelled) setName(list.find((h) => h.date === date)?.localName ?? null); })
      .catch(() => { if (!cancelled) setName(null); });
    return () => { cancelled = true; };
  }, [date, timezone]);
  return name;
}

type Estado = "cargando" | "bloqueado" | "trabaja" | "pendiente";

/**
 * Marca del feriado en el día de la Agenda (semanal, diaria y mensual, en la web y en la app):
 * el nombre del feriado en chiquito. Al tocarla se abren las mismas opciones del aviso de
 * arriba — "Bloquear mi agenda" / "Voy a trabajar" — y, si ya había dicho "Voy a trabajar",
 * puede bloquear igual (eso borra el "Voy a trabajar"). Es la forma de cambiar de opinión
 * después de que el aviso se fue.
 * Si el día no es feriado, no renderiza nada.
 */
export const HolidayDayMarker = ({ date, variant = "chip" }: Props) => {
  const psychologist = usePsychologistData();
  const timezone =
    psychologist?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Argentina/Buenos_Aires";
  const name = useHolidayName(date, timezone);
  const { bloquear, checking, dialog } = useBlockHoliday(timezone);
  const [open, setOpen] = useState(false);
  const [estado, setEstado] = useState<Estado>("cargando");

  // Al abrir: ¿ya bloqueó el día o dijo "Voy a trabajar"?
  useEffect(() => {
    if (!open || !name) return;
    let cancelled = false;
    const revisar = async () => {
      setEstado("cargando");
      const [bloqueado, trabaja] = await Promise.all([
        isHolidayFullyBlocked(date, timezone).catch(() => false),
        loadFeriadosTrabaja(),
      ]);
      if (!cancelled) setEstado(bloqueado ? "bloqueado" : trabaja.has(date) ? "trabaja" : "pendiente");
    };
    revisar();
    window.addEventListener(FERIADOS_CHANGED_EVENT, revisar);
    return () => {
      cancelled = true;
      window.removeEventListener(FERIADOS_CHANGED_EVENT, revisar);
    };
  }, [open, name, date, timezone]);

  if (!name) return null;

  const label = `Feriado: ${name}`;
  if (variant === "dot") {
    return (
      <span
        data-testid={`holiday-mark-${date}`}
        title={label}
        className="mt-1 block h-[3px] w-4 rounded bg-amber-500"
      />
    );
  }

  const abrir = (e: MouseEvent) => {
    e.stopPropagation();
    activityTracker.trackAction("feriado_marca_abierta");
    setOpen(true);
  };

  const onBloquear = async () => {
    setOpen(false);
    // Cambió de idea: deja de valer "Voy a trabajar" (si borra el bloqueo, el aviso vuelve).
    if (estado === "trabaja") await setFeriadoTrabaja(date, false);
    bloquear(date, name);
  };

  const onTrabajar = async () => {
    // Si había abierto el panel de bloqueo, se cierra solo (igual que en el aviso).
    window.dispatchEvent(new CustomEvent("psi-block-close"));
    activityTracker.trackAction("feriado_voy_a_trabajar");
    await setFeriadoTrabaja(date, true);
    setOpen(false);
  };

  const trigger =
    variant === "banner" ? (
      <button
        type="button"
        data-testid={`holiday-mark-${date}`}
        onClick={abrir}
        className="mb-3 flex w-full items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-left text-sm text-amber-800 hover:bg-amber-100"
      >
        <span className="min-w-0 truncate font-semibold">{label}</span>
        <span className="shrink-0 text-xs font-semibold underline">Ver opciones</span>
      </button>
    ) : (
      <button
        type="button"
        data-testid={`holiday-mark-${date}`}
        onClick={abrir}
        title={label}
        className="mt-1 block max-w-full truncate rounded-full bg-amber-100 px-2 py-0.5 text-left text-[0.65rem] font-semibold text-amber-800 hover:bg-amber-200"
      >
        {label}
      </button>
    );

  return (
    <>
      {trigger}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex flex-col gap-4 p-6">
          <DialogHeader className="mb-0">
            <DialogTitle className="m-0 text-[#2D2049]">{name}</DialogTitle>
          </DialogHeader>
          <p className="m-0 -mt-2 text-xs text-gray-500">
            Feriado en {countryNameFromTimezone(timezone) ?? "tu país"} · {formatHolidayDay(date)}
          </p>
          {estado === "cargando" ? (
            <div className="flex justify-center py-3"><CircularProgress /></div>
          ) : estado === "bloqueado" ? (
            <>
              <p className="m-0 text-sm leading-relaxed text-[#4A3D73]">
                Ya bloqueaste tu agenda este día. Si cambiás de idea, borrá el bloqueo desde la Agenda.
              </p>
              <div className="flex justify-end">
                <Button className="bg-[#665090] text-white hover:bg-[#665090]/90" onClick={() => setOpen(false)}>
                  Entendido
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="m-0 text-sm leading-relaxed text-[#4A3D73]">
                {estado === "trabaja"
                  ? "Marcaste que vas a trabajar este día. Si cambiaste de idea, bloqueá tu agenda."
                  : "¿Vas a trabajar? Si no, bloqueá tu agenda para que no te agenden sesiones ese día."}
              </p>
              <div className="flex flex-wrap justify-end gap-2">
                {estado !== "trabaja" && (
                  <Button variant="outline" className="border-amber-300 text-amber-700" onClick={onTrabajar}>
                    Voy a trabajar
                  </Button>
                )}
                <Button
                  className="bg-amber-600 hover:bg-amber-600/90"
                  onClick={onBloquear}
                  disabled={checking !== null}
                >
                  {checking === date ? "Revisando tu agenda…" : "Bloquear mi agenda"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {dialog}
    </>
  );
};

export default HolidayDayMarker;
